import {
  validateLowQuantityBalanceThresholdAbsentUnlessIsInventory,
  validateOverwriteIfProductNameExists,
  validateOverwriteIfRemovingGlCode,
} from "../hooks"

// Mirrors the Ruby-side constant
// (DromoImports::Processors::ItemProcessor::REMOVE_GL_CODE_VALUE).
// Used here for client-side row validation; the value flows through the
// payload via `generalLedgerCodes` for the dropdown itself.
export const REMOVE_GL_CODE_VALUE = "REMOVE_GL_CODE"

export const getItemImportConfig = ({
  itemizedTaxEnabled,
  servicePartsEnabled,
  productCategories,
  productNames,
  generalLedgerCodes,
}) => {
  const availabilityKeys = [
    "availability_contract_reservation_add_ons",
    "availability_point_of_sale",
    ...(servicePartsEnabled ? ["availability_service_parts"] : []),
  ]
  const availabilityLabels = [
    "contract/reservation add-on",
    "point of sale",
    ...(servicePartsEnabled ? ["service parts"] : []),
  ]
  const availabilityErrorMessage = `Item must be enabled for at least one availability: ${availabilityLabels.join(
    ", "
  )}`

  // require_with_all_values is the AND variant — required if and only if
  // every listed peer matches. require_with_values is OR, which silently
  // flags fields when only one peer is false.
  const buildAvailabilityValidator = (currentField) => ({
    validate: "require_with_all_values",
    fieldValues: availabilityKeys
      .filter((key) => key !== currentField)
      .reduce((acc, key) => {
        acc[key] = false
        return acc
      }, {}),
    errorMessage: availabilityErrorMessage,
  })

  const baseFields = [
    {
      label: "Item Name",
      key: "name",
      type: "string",
      validators: [
        { validate: "required" },
        {
          validate: "unique_case_insensitive",
          error: "Duplicate item name in import",
        },
      ],
    },
    {
      label: "SKU",
      key: "sku",
      type: "string",
      description:
        "If left blank when overwriting an existing item, it will clear the existing SKU if one exists.",
    },
    {
      label: "Default Pricing Type",
      key: "default_pricing_structure",
      type: "select",
      selectOptions: [
        { value: "", label: "" },
        { value: "per_day", label: "Per day", alternateMatches: ["Per day"] },
        {
          value: "per_quantity",
          label: "Per quantity",
          alternateMatches: ["Per quantity"],
        },
      ],
      description:
        "If left blank when overwriting an existing item, or adding a new one, the value will become the default 'per_quantity' pricing structure.",
    },
    {
      label: "Default Price",
      key: "price_per_unit",
      type: [
        "number",
        {
          preset: "plain",
          displayFormat: {
            thousandSeparated: true,
            output: "currency",
            currencySymbol: "$",
            currencyPosition: "prefix",
          },
          outputFormat: {
            thousandSeparated: false,
            output: "number",
          },
          min: 0,
        },
      ],
      description:
        "The price (price_per_unit) in US dollars, maximum 2 decimal places.",
      validators: [{ validate: "required" }],
    },
    {
      label: "Default Tax",
      key: "tax_rate",
      type: ["number", { preset: "percent", min: 0, max: 1 }],
      description:
        "Tax rate with up to 4 digits of precision, eg. 0.075125 or 7.5125% (with percent sign)",
      validators: [{ validate: "required" }],
    },
    {
      label: "Category",
      key: "product_category_name",
      type: ["select", { allowCustom: true }],
      selectOptions: productCategories,
      description:
        "The item category — select an existing category or enter the name of a new category to be created. If left blank when overwriting an existing product, it will clear the existing product category if one exists.",
      validators: [
        {
          validate: "regex_exclude",
          regex: "^uncategorized$",
          regexOptions: {
            ignoreCase: true,
          },
          errorMessage: "Uncategorized is not a valid category.",
        },
      ],
    },
    ...(generalLedgerCodes?.length > 0
      ? [
          {
            label: "G/L Code",
            key: "gl_code",
            type: "select",
            selectOptions: generalLedgerCodes,
            description:
              'Map this item to a general ledger code. Leave blank to leave any existing mapping unchanged, even if overwrite is true. Pick "Remove Existing G/L Code" to clear an existing mapping.',
          },
        ]
      : []),
    {
      label: "Track Inventory",
      key: "is_inventory",
      type: "checkbox",
      description:
        "Treat this item as inventory (item quantities to be managed separately). If left unchecked when overwriting an existing item, or adding a new one, it will be set to false.",
    },
    {
      label: "Low Balance Point",
      key: "low_quantity_balance_threshold",
      type: ["number", { preset: "integer", min: 0 }],
      description:
        "The quantity at which the item should be considered low-balance (to show an indicator in item management). If left blank when overwriting an existing item, it will clear the existing value if one exists.",
    },
    {
      label: "Default Recurrence",
      key: "default_recurrence",
      type: "select",
      selectOptions: [
        { value: "", label: "" },
        {
          value: "does_not_repeat",
          label: "Does not repeat",
          alternateMatches: ["Does not repeat"],
        },
        {
          value: "repeats_monthly",
          label: "Repeats monthly",
          alternateMatches: ["Repeats monthly"],
        },
      ],
      description:
        "If left blank when overwriting an existing item, or adding a new one, the item will be set to non-recurring.",
    },
    {
      label: "Available as Contract/Reservation Add-On?",
      key: "availability_contract_reservation_add_ons",
      type: "checkbox",
      requireMapping: true,
      description:
        "This item should be available as a contract or reservation add-on.",
      validators: [
        buildAvailabilityValidator("availability_contract_reservation_add_ons"),
      ],
    },
    {
      label: "Available in Point of Sale?",
      key: "availability_point_of_sale",
      type: "checkbox",
      requireMapping: true,
      description: "This item should be available in Point of Sale.",
      validators: [buildAvailabilityValidator("availability_point_of_sale")],
    },
    ...(servicePartsEnabled
      ? [
          {
            label: "Available in Service Parts?",
            key: "availability_service_parts",
            type: "checkbox",
            requireMapping: true,
            description:
              "This item should be available as a service work order part.",
            validators: [
              buildAvailabilityValidator("availability_service_parts"),
            ],
          },
        ]
      : []),
    {
      label: "Overwrite",
      key: "overwrite",
      type: "checkbox",
      description:
        "For existing items only, should the data in this import overwrite the existing item's attributes with those provided here?",
    },
    {
      label: "Barcode",
      key: "barcode",
      type: "string",
      description:
        "If left blank when overwriting an existing item, it will clear the existing barcode if one exists.",
      validators: [
        {
          validate: "unique",
          error: "Duplicate barcode in import",
        },
      ],
    },
  ]

  let fields = baseFields
  if (itemizedTaxEnabled) {
    fields = fields.filter((field) => field.key !== "tax_rate")
  }

  const rowHooks = [
    validateLowQuantityBalanceThresholdAbsentUnlessIsInventory,
    validateOverwriteIfProductNameExists(productNames),
    validateOverwriteIfRemovingGlCode(REMOVE_GL_CODE_VALUE),
  ]

  return { fields, rowHooks }
}
